// Ch94 · THE POSTING CALENDAR — built on the Leads grid, not beside it.
//
// Grant pointed at the Leads page: *"see how it combined multiple ideas into
// widgets"*. The packet: REUSE `LeadsMoveInCalendar` + `leadsCalendar.ts`, do
// not rebuild a calendar. So the month/week grid — padding days, today,
// weekends, the "8" vs "Aug 30" labelling — comes from `buildCalendarGrid`,
// which was extracted from `buildCalendar` unchanged. Reels hang on the days
// instead of people.
//
// 🔴 WHAT THIS MODULE REFUSES TO DO:
//  · it never SCHEDULES anything. `proposeWeek` returns a PROPOSAL Grant
//    confirms. The packet is explicit — "nothing is scheduled silently".
//  · it never reports a posted count it cannot read. `content_posts` is the
//    only source; when it has not been read the answer is UNKNOWN, not 0.
//  · it never claims a day is "missed" in the FUTURE. A day with no post is
//    only a gap once it has passed.

import { buildCalendarGrid, type CalendarCell, type CalendarMode } from "./leadsCalendar";
import { counted, unknown, type SocialsCount } from "./socialsCounts";

/** A reel scheduled to go out. Phase 4 supplies these; none exist yet. */
export interface ScheduledPost {
  slotId?: string;
  status?: "scheduled" | "cancelled" | "fired" | "missed" | "refused";
  resolutionCode?: string | null;
  deliveryState?: string | null;
  reviewId: string;
  /** ISO timestamp of the slot. */
  scheduledFor: string;
  subject: string;
  template: string;
}

/** "9:00a · Grace" — the mock's chip text: when, then who, in a day cell's width. */
export function slotChipLabel(slot: ScheduledPost): string {
  const parsed = Date.parse(slot.scheduledFor);
  const short = slot.subject.split(/[—·-]/)[0].trim().split(" ").slice(0, 2).join(" ");
  if (!Number.isFinite(parsed)) return short;
  const d = new Date(parsed);
  const hour = d.getHours() % 12 || 12;
  const mins = d.getMinutes();
  const suffix = d.getHours() < 12 ? "a" : "p";
  return `${hour}:${String(mins).padStart(2, "0")}${suffix} · ${short}`;
}

/** A reel already posted, from `content_posts` (migration 091, applied). */
export interface PublishedPost {
  reviewId: string;
  postedAt: string;
  subject: string;
  template: string;
}

export interface PostingDay extends CalendarCell {
  /** Dashed on screen. */
  scheduled: ScheduledPost[];
  /** Filled on screen. */
  posted: PublishedPost[];
  /** True only for a PAST in-month day that holds neither. */
  isGap: boolean;
}

export interface PostingCalendar {
  label: string;
  weeks: PostingDay[][];
  todayMs: number;
  /** Past in-month days with nothing on them. */
  gapCount: number;
  /** In-month days that hold a posted reel. */
  postedCount: number;
  /** In-month days that hold a scheduled reel. */
  scheduledCount: number;
}

const dayKey = (iso: string): number | null => {
  const parsed = Date.parse(iso);
  if (!Number.isFinite(parsed)) return null;
  const d = new Date(parsed);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

function bucket<T>(rows: T[], iso: (row: T) => string): Map<number, T[]> {
  const out = new Map<number, T[]>();
  rows.forEach((row) => {
    const key = dayKey(iso(row));
    if (key === null) return; // an unparseable date is dropped, never placed on "today"
    const existing = out.get(key);
    if (existing) existing.push(row);
    else out.set(key, [row]);
  });
  return out;
}

export function buildPostingCalendar(
  scheduled: ScheduledPost[],
  posted: PublishedPost[],
  opts: { anchor: Date; mode: CalendarMode; now: Date }
): PostingCalendar {
  const grid = buildCalendarGrid(opts);
  const scheduledByDay = bucket(scheduled, (s) => s.scheduledFor);
  const postedByDay = bucket(posted, (p) => p.postedAt);

  let gapCount = 0;
  let postedCount = 0;
  let scheduledCount = 0;

  const weeks: PostingDay[][] = grid.weeks.map((week) =>
    week.map((cell) => {
      const daysScheduled = scheduledByDay.get(cell.ms) ?? [];
      const daysPosted = postedByDay.get(cell.ms) ?? [];
      const activeScheduled = daysScheduled.filter(s => !s.status || s.status === "scheduled");
      // 🔴 A FUTURE DAY IS NOT A GAP. Calling tomorrow "missed" would turn a
      // plan into a reprimand, and every empty month would open bright red.
      const isGap =
        cell.inMonth &&
        cell.ms < grid.todayMs &&
        activeScheduled.length === 0 &&
        daysPosted.length === 0;

      if (cell.inMonth) {
        if (isGap) gapCount += 1;
        if (daysPosted.length) postedCount += 1;
        if (activeScheduled.length) scheduledCount += 1;
      }

      return { ...cell, scheduled: daysScheduled, posted: daysPosted, isGap };
    })
  );

  return {
    label: grid.label,
    weeks,
    todayMs: grid.todayMs,
    gapCount,
    postedCount,
    scheduledCount,
  };
}

/**
 * Posts per week against a goal.
 *
 * `posted` is `null` when `content_posts` has not been read — which renders
 * "—", never 0. On 2026-09-19 the real count IS zero, and a real zero is a
 * different statement from an unread one.
 */
export interface ConsistencyWeek {
  weekStartMs: number;
  count: SocialsCount;
}

export function weeklyConsistency(
  posted: PublishedPost[] | null,
  opts: { weeks: number; now: Date }
): ConsistencyWeek[] {
  const out: ConsistencyWeek[] = [];
  const now = opts.now;
  const thisWeekStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - now.getDay()
  ).getTime();
  const WEEK_MS = 7 * 86_400_000;

  for (let i = opts.weeks - 1; i >= 0; i -= 1) {
    const start = thisWeekStart - i * WEEK_MS;
    if (posted === null) {
      out.push({ weekStartMs: start, count: unknown() });
      continue;
    }
    const n = posted.filter((p) => {
      const key = dayKey(p.postedAt);
      return key !== null && key >= start && key < start + WEEK_MS;
    }).length;
    out.push({ weekStartMs: start, count: counted(n) });
  }
  return out;
}

/**
 * Open days from today through the next `days` — ACROSS the month boundary.
 *
 * PKT-0912: auto-fill used to take its days from the calendar grid's ANCHOR
 * month only, so on the last day of a month after 09:00 there were none and
 * "Auto-fill this week" silently did nothing. The week does not stop at the
 * 1st. A day is open when it holds nothing scheduled and nothing posted.
 */
export function openDaysAhead(
  scheduled: ScheduledPost[],
  posted: PublishedPost[],
  now: Date,
  days = 7
): number[] {
  const taken = new Set<number>();
  scheduled.forEach((s) => {
    const key = dayKey(s.scheduledFor);
    if (key !== null) taken.add(key);
  });
  posted.forEach((p) => {
    const key = dayKey(p.postedAt);
    if (key !== null) taken.add(key);
  });
  const out: number[] = [];
  for (let i = 0; i <= days; i += 1) {
    const ms = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i).getTime();
    if (!taken.has(ms)) out.push(ms);
  }
  return out;
}

/** A reel that could go in a slot. */
export interface SchedulableReel {
  versionKey?: string;
  reviewId: string;
  subject: string;
  template: string;
  /** What it's about — used to avoid proposing the same building twice running. */
  about: string | null;
  /** "18s" for the rail. Absent when we cannot say — never a guessed number. */
  durationLabel?: string | null;
}

export interface ProposedSlot {
  reel: SchedulableReel;
  dayMs: number;
}

/**
 * Propose a spread for the week — MIXING types and buildings.
 *
 * 🔴 THIS SCHEDULES NOTHING. It returns a proposal for Grant to confirm.
 *
 * The mixing rule is the point: three property tours for the same building on
 * consecutive days is technically "a spread" and is obviously wrong, so a reel
 * is skipped for a day when it repeats the previous day's TYPE or SUBJECT and
 * another candidate would not.
 */
export function proposeWeek(
  reels: SchedulableReel[],
  openDays: number[],
  opts: { maxPerWeek: number }
): ProposedSlot[] {
  const days = [...openDays].sort((a, b) => a - b).slice(0, opts.maxPerWeek);
  const remaining = [...reels];
  const proposals: ProposedSlot[] = [];

  let lastTemplate: string | null = null;
  let lastAbout: string | null = null;

  days.forEach((dayMs) => {
    if (remaining.length === 0) return;
    // Prefer a reel that differs from yesterday on BOTH axes; fall back to one
    // that differs on either; only then take whatever is left.
    const pick =
      remaining.findIndex((r) => r.template !== lastTemplate && r.about !== lastAbout) >= 0
        ? remaining.findIndex((r) => r.template !== lastTemplate && r.about !== lastAbout)
        : remaining.findIndex((r) => r.template !== lastTemplate || r.about !== lastAbout) >= 0
          ? remaining.findIndex((r) => r.template !== lastTemplate || r.about !== lastAbout)
          : 0;

    const [reel] = remaining.splice(pick, 1);
    proposals.push({ reel, dayMs });
    lastTemplate = reel.template;
    lastAbout = reel.about;
  });

  return proposals;
}
